import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';
import * as Crypto from 'expo-crypto';
import { Platform } from 'react-native';

const DEVICE_ID_STORAGE_KEY = '@sendifier:device-id';

let cachedDeviceId: string | null = null;
let inFlight: Promise<string> | null = null;

async function isSecureStoreAvailable(): Promise<boolean> {
  try {
    return !!(SecureStore?.isAvailableAsync && (await SecureStore.isAvailableAsync()));
  } catch {
    return false;
  }
}

async function readStoredDeviceId(): Promise<string | null> {
  if (Platform.OS !== 'web' && (await isSecureStoreAvailable())) {
    try {
      const value = await SecureStore.getItemAsync(DEVICE_ID_STORAGE_KEY);
      if (value) return value;
    } catch {
      // fall through to AsyncStorage
    }
  }
  try {
    return await AsyncStorage.getItem(DEVICE_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

async function writeStoredDeviceId(id: string): Promise<void> {
  if (Platform.OS !== 'web' && (await isSecureStoreAvailable())) {
    try {
      await SecureStore.setItemAsync(DEVICE_ID_STORAGE_KEY, id);
      return;
    } catch {
      // fall through to AsyncStorage
    }
  }
  try {
    await AsyncStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
  } catch {
    // best-effort — a failed persist just means a fresh id gets generated next launch
  }
}

/**
 * Stable per-install device identifier, used to scope refresh-token sliding windows
 * (trusted vs untrusted) to a single physical device/app-install rather than the user
 * account as a whole — so refreshing on one device never invalidates another device's session.
 */
export async function getDeviceId(): Promise<string> {
  if (cachedDeviceId) return cachedDeviceId;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const existing = await readStoredDeviceId();
    if (existing) {
      cachedDeviceId = existing;
      return existing;
    }
    const id = Crypto.randomUUID();
    await writeStoredDeviceId(id);
    cachedDeviceId = id;
    return id;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}
